import {getBackendUrl} from 'utilities';

export interface ServiceNowNavMenuNode {
  id: string;
  title: string;
  sort_order: number;
  url?: string | null;
  children: ServiceNowNavMenuNode[];
}

export interface ServiceNowNavCategory {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
  pages: ServiceNowNavMenuNode[];
}

export interface ServiceNowNavMenuResponse {
  categories: ServiceNowNavCategory[];
}

export interface ServiceNowKnowledgePageNode {
  id: string;
  title: string;
  node_type?: string;
  is_container: boolean;
  parent_page_id?: string | null;
  sort_order: number;
  body?: string | null;
  primary_category_slug?: string | null;
  url?: string | null;
  updated_on?: string | null;
  internal_page_id?: string | null;
  children: ServiceNowKnowledgePageNode[];
}

export interface ServiceNowKnowledgePagesResponse {
  categories: ServiceNowNavCategory[];
  pages: ServiceNowKnowledgePageNode[];
}

const KNOWLEDGE_BASE_PATH = `${getBackendUrl()}/api/v1/knowledge`;

const toError = async (response: Response) => {
  let detail = `ServiceNow content request failed: ${response.status}`;
  try {
    const parsed = await response.json();
    if (parsed?.detail && typeof parsed.detail === 'string') {
      detail = parsed.detail;
    }
  } catch {
    // no-op
  }
  throw new Error(detail);
};

const request = async <T>(url: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(url, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!response.ok) {
    await toError(response);
  }
  return response.json() as Promise<T>;
};

export const fetchKnowledgeNavMenu = () =>
  request<ServiceNowNavMenuResponse>(`${KNOWLEDGE_BASE_PATH}/nav-menu`);

export const fetchServiceNowKnowledgePages = (categorySlug?: string | null) => {
  const params = new URLSearchParams();
  if (categorySlug?.trim()) {
    params.set('category_slug', categorySlug.trim());
  }
  const query = params.toString();
  return request<ServiceNowKnowledgePagesResponse>(
    `${KNOWLEDGE_BASE_PATH}/servicenow/pages${query ? `?${query}` : ''}`
  );
};

export const fetchServiceNowKnowledgePage = (id: string) =>
  request<ServiceNowKnowledgePageNode>(
    `${KNOWLEDGE_BASE_PATH}/servicenow/pages/${encodeURIComponent(id)}`
  );

/**
 * Reports an article view to ServiceNow's Knowledge Use table. Company OS
 * serves articles from our own synced copy, so ServiceNow only learns about
 * the read if we tell it. Responds 204 with no body.
 */
export const logServiceNowKnowledgePageView = async (id: string): Promise<void> => {
  const response = await fetch(
    `${KNOWLEDGE_BASE_PATH}/servicenow/pages/${encodeURIComponent(id)}/view`,
    {method: 'POST', credentials: 'include'}
  );

  if (!response.ok) {
    await toError(response);
  }
};
